/**
 * Tiny Web Audio "earcons" for the POS register — short synthesized tones, so
 * there are no audio asset files (keeps the frozen lockfile + offline story
 * simple) and zero network. Muteable; the preference persists in localStorage.
 *
 * The AudioContext is created lazily and resumed on demand — the first sound
 * plays after a user gesture (a tap/scan), which satisfies autoplay policies.
 */
export type PosSound = 'add' | 'remove' | 'tick' | 'success' | 'error';

const MUTE_KEY = 'pos.soundMuted';

type WebkitWindow = typeof window & { webkitAudioContext?: typeof AudioContext };

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function isSoundMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSoundMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore (private mode, etc.) */
  }
}

/** One short tone with a click-free attack/release envelope. */
function tone(
  c: AudioContext,
  freq: number,
  startAt: number,
  dur: number,
  type: OscillatorType,
  peak = 0.14,
): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const t0 = c.currentTime + startAt;
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

// ---------------------------------------------------------------------------
// Staff-bell notification chime — its own mute preference (independent of the
// POS register earcons; a cashier may want scanner beeps but a silent bell).

const NOTIFY_MUTE_KEY = 'notifications.soundMuted';

export function isNotifySoundMuted(): boolean {
  try {
    return localStorage.getItem(NOTIFY_MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setNotifySoundMuted(muted: boolean): void {
  try {
    localStorage.setItem(NOTIFY_MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore (private mode, etc.) */
  }
}

/** Gentle two-note "ding" for an incoming staff notification. */
export function playNotificationSound(): void {
  if (isNotifySoundMuted()) return;
  const c = audio();
  if (!c) return;
  tone(c, 988, 0, 0.12, 'sine', 0.11);
  tone(c, 1319, 0.11, 0.2, 'sine', 0.09);
}

export function playSound(name: PosSound): void {
  if (isSoundMuted()) return;
  const c = audio();
  if (!c) return;
  switch (name) {
    case 'add': // bright blip
      tone(c, 880, 0, 0.1, 'sine', 0.16);
      break;
    case 'tick': // soft, short — qty +/-
      tone(c, 660, 0, 0.05, 'sine', 0.1);
      break;
    case 'remove': // descending blip
      tone(c, 440, 0, 0.1, 'sine', 0.14);
      tone(c, 300, 0.06, 0.12, 'sine', 0.12);
      break;
    case 'success': // two-note up chime (sale finalized)
      tone(c, 660, 0, 0.1, 'sine', 0.16);
      tone(c, 988, 0.1, 0.18, 'sine', 0.16);
      break;
    case 'error': // low buzz
      tone(c, 200, 0, 0.2, 'square', 0.12);
      tone(c, 160, 0.1, 0.22, 'square', 0.12);
      break;
  }
}
